import { useCallback, useEffect, useMemo, useState } from 'react';
import { fiscalYearOptions } from '../../utils/fiscalYear.mjs';

/**
 * Load one period of a Reports tab, and reload when the period changes.
 *
 * The period and region belong to ReportsPage (held in the URL so they
 * survive tab switches), so `load()` takes no arguments: wrap it in
 * useCallback with the scope as its dependencies, and a change of year,
 * quarter or region reloads it.
 *
 * `fiscalYear` is the year the page asked for. The year list comes from the
 * server when it sends one; otherwise the last five years, always including
 * the one selected so a deep link to an older year still shows in the picker.
 */
export const useFiscalReport = (load, fiscalYear) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setStatus(null);
    load()
      .then(d => { if (alive) setData(d); })
      .catch(err => { if (alive) { setError(err.message); setStatus(err.status || null); } })
      .finally(() => { if (alive) setLoading(false); });
    // A late answer for a period no longer selected must not overwrite the
    // one that is.
    return () => { alive = false; };
  }, [load, attempt]);

  const reload = useCallback(() => setAttempt(a => a + 1), []);

  const fiscalYears = useMemo(() => {
    const years = new Set(data?.fiscalYears?.length ? data.fiscalYears : fiscalYearOptions(5));
    if (fiscalYear) years.add(Number(fiscalYear));
    return [...years].sort((a, b) => b - a);
  }, [data, fiscalYear]);

  return { data, loading, error, status, fiscalYears, reload };
};

export default useFiscalReport;
